import {
  createEntityAdapter,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';

import type { TemplateSnapshot, TemplateState } from './template.types';
import type { RootState } from '../../redux-store';

// Entity adapter for normalized template storage
const templateAdapter = createEntityAdapter<TemplateSnapshot, string>({
  selectId: template => template.id,
  sortComparer: (a, b) => {
    // System templates first, then by name alphabetically
    if (a.isCustom !== b.isCustom) {
      return a.isCustom ? 1 : -1;
    }
    return a.name.localeCompare(b.name);
  },
});

// Initial state
const initialState: TemplateState = {
  templates: templateAdapter.getInitialState(),
  activeTemplateId: null,
  activeTemplateName: null,
  activeTemplateIsCustom: false,
  previewTemplateId: null,
  isApplying: false,
};

// Slice
export const templateSlice = createSlice({
  name: 'template',
  initialState,
  reducers: {
    /**
     * Set the active template (called after successful apply)
     */
    setActiveTemplate: (
      state,
      action: PayloadAction<{
        id: string;
        name: string;
        isCustom: boolean;
      }>,
    ) => {
      const { id, name, isCustom } = action.payload;
      state.activeTemplateId = id;
      state.activeTemplateName = name;
      state.activeTemplateIsCustom = isCustom;
    },
    /**
     * Set the preview template ID (template loaded but not yet marked as active)
     */
    setPreviewTemplate: (state, action: PayloadAction<string | null>) => {
      state.previewTemplateId = action.payload;
    },
    /**
     * Update a template's data in the entity store
     */
    updateTemplate: (state, action: PayloadAction<TemplateSnapshot>) => {
      templateAdapter.upsertOne(state.templates, action.payload);
    },
    /**
     * Clear preview state
     */
    clearPreview: state => {
      state.previewTemplateId = null;
    },
  },
  extraReducers: builder => {
    // Handle applyTemplate async states
    builder.addMatcher(
      action => action.type === 'template/apply/pending',
      state => {
        state.isApplying = true;
      },
    );
    builder.addMatcher(
      action => action.type === 'template/apply/fulfilled',
      state => {
        state.isApplying = false;
      },
    );
    builder.addMatcher(
      action => action.type === 'template/apply/rejected',
      state => {
        state.isApplying = false;
      },
    );
  },
});

// Selectors using entity adapter
const adapterSelectors = templateAdapter.getSelectors<RootState>(
  state => state.template.templates,
);

export const templateSelectors = {
  // Entity adapter selectors
  selectAll: adapterSelectors.selectAll,
  selectById: adapterSelectors.selectById,
  selectIds: adapterSelectors.selectIds,
  selectEntities: adapterSelectors.selectEntities,
  selectTotal: adapterSelectors.selectTotal,

  // Custom selectors
  selectActiveTemplateId: (state: RootState) => state.template.activeTemplateId,
  selectActiveTemplateName: (state: RootState) =>
    state.template.activeTemplateName,
  selectActiveTemplateIsCustom: (state: RootState) =>
    state.template.activeTemplateIsCustom,
  selectPreviewTemplateId: (state: RootState) =>
    state.template.previewTemplateId,
  selectIsApplying: (state: RootState) => state.template.isApplying,
};

export const templateActions = templateSlice.actions;
